import { useCalendarModal } from '@forwardflow/store/modal/calendarModal'
import { Button } from '@forwardflow/ui/Button'
import { Modal, message } from 'antd'
import { useState, useEffect } from 'react'
import { Spinner } from '@forwardflow/ui'
import { UserService } from '@forwardflow/services/UserService'
import { Calendar } from '@forwardflow/types'
import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'
import { useGetCalendarList } from '@forwardflow/hooks/customHooks/useGetCalendarList'

interface CalendarModalProps {
  onCalendarUpdate?: () => void
  linkedCalendars?: Calendar[]
  connectedEmail?: string
}

export const CalendarModal = ({ onCalendarUpdate, linkedCalendars = [], connectedEmail }: CalendarModalProps) => {
  const { data: user } = useAuthUser({ revalidateOnMount: false });
  const { isCalendarModalOpen, closeCalendarModal } = useCalendarModal()

  const [isSubmitLoading, setIsSubmitLoading] = useState(false)
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>([])

  const { data: calendars, isLoading } = useGetCalendarList(isCalendarModalOpen)

  // Initialize selected calendars when modal opens
  useEffect(() => {
    if (isCalendarModalOpen && calendars && calendars.length > 0) {
      if (linkedCalendars && linkedCalendars.length > 0) {
        const initialSelected: string[] = []

        linkedCalendars.forEach(linkedCal => {
          if (linkedCal.id?.toLowerCase() === 'primary' || linkedCal.summary?.toLowerCase() === 'primary') {
            const defaultCalendar = calendars.find(
              (cal: Calendar) =>
                cal.id?.toLowerCase() === connectedEmail?.toLowerCase() ||
                cal.summary?.toLowerCase() === connectedEmail?.toLowerCase()
            )
            if (defaultCalendar) {
              initialSelected.push(defaultCalendar.id)
            }
          } else {
            const matchingCalendar = calendars.find((cal: Calendar) => cal.id === linkedCal.id)
            if (matchingCalendar) {
              initialSelected.push(matchingCalendar.id)
            }
          }
        })

        setSelectedCalendarIds(initialSelected)
      } else {
        const primaryCalendar = calendars.find(
          (cal: Calendar) =>
            cal.id?.toLowerCase() === 'primary' ||
            cal.summary?.toLowerCase() === 'primary' ||
            cal.id?.toLowerCase() === connectedEmail?.toLowerCase() ||
            cal.summary?.toLowerCase() === connectedEmail?.toLowerCase()
        )
        if (primaryCalendar) {
          setSelectedCalendarIds([primaryCalendar.id])
        }
      }
    }
  }, [isCalendarModalOpen, linkedCalendars, calendars, connectedEmail])

  // Reset when modal closes
  useEffect(() => {
    if (!isCalendarModalOpen) {
      setSelectedCalendarIds([])
    }
  }, [isCalendarModalOpen])

  const hasNoCalendars = !isLoading && (!calendars || calendars.length === 0)

  const handleSelectAll = () => {
    if (!calendars) return

    const allSelected = selectedCalendarIds.length === calendars.length
    if (allSelected) {
      setSelectedCalendarIds([])
    } else {
      setSelectedCalendarIds(calendars.map((cal: Calendar) => cal.id))
    }
  }

  const handleCalendarToggle = (id: string) => {
    setSelectedCalendarIds(prev =>
      prev.includes(id)
        ? prev.filter(calId => calId !== id)
        : [...prev, id]
    )
  }

  const handleConfirm = async () => {
    if (selectedCalendarIds.length === 0) {
      message.warning('Please select at least one calendar')
      return
    }

    if (!calendars) return

    setIsSubmitLoading(true)
    try {
      const preferredCalendars = calendars
        .filter(cal => selectedCalendarIds.includes(cal.id))
        .map(cal => ({
          id: cal.id,
          summary: cal.summary
        }))

      await UserService.setPreferredCalendarList(user?.email || '', preferredCalendars)

      message.success('Calendars linked successfully')

      if (onCalendarUpdate) {
        await onCalendarUpdate()
      }

      closeCalendarModal()
      setSelectedCalendarIds([])
    } catch (error) {
      console.error('Error updating calendars:', error)
      message.error('Failed to update calendars')
    } finally {
      setIsSubmitLoading(false)
    }
  }

  const handleClose = () => {
    closeCalendarModal()
    setSelectedCalendarIds([])
  }

  const EmptyStateComponent = () => {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-[16px] font-semibold text-white mb-2">
          No linked calendars found
        </p>
        <p className="text-[14px] text-white/70 mb-28 text-center">
          We didn't find any calendars available to add right now.
        </p>
        <Button
          onClick={handleClose}
          className="!bg-white/10 !border-0 hover:!bg-white/20 !w-auto px-8"
        >
          Close
        </Button>
      </div>
    )
  }

  return (
    <Modal
      className="ff-modal"
      title={<span className="font-semibold">Add Calendars</span>}
      open={isCalendarModalOpen}
      onCancel={handleClose}
      footer={null}
    >
      <>
        <div className="flex flex-col">
          <p className="text-[12px] font-medium mb-4">
            Select which calendars you want Forward Flow to check Availabilities.
            <br />
            Note: Appointments will only be scheduled in your Primary Calendar
          </p>
        </div>

        <div className="border-t border-white/20 -mx-6" />

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : hasNoCalendars ? (
          <div className="mt-4">
            <EmptyStateComponent />
          </div>
        ) : (
          <div className="flex flex-col mt-4">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={handleSelectAll}
                className="text-[12px] text-white/70 hover:text-white font-medium"
              >
                Select All
              </button>
              <span className="text-[12px] text-[#818181]">
                Selected {selectedCalendarIds.length}/{calendars?.length || 0}
              </span>
            </div>

            <div className="flex flex-col gap-y-2 mb-6">
              {calendars?.map((calendar: Calendar) => {
                const isChecked = selectedCalendarIds.includes(calendar.id)
                const isDefaultCalendar =
                  calendar.id?.toLowerCase() === 'primary' ||
                  calendar.summary?.toLowerCase() === 'primary' ||
                  calendar.id?.toLowerCase() === connectedEmail?.toLowerCase() ||
                  calendar.summary?.toLowerCase() === connectedEmail?.toLowerCase()
                const displayName = isDefaultCalendar ? 'Default' : calendar.summary
                return (
                  <div
                    key={calendar.id}
                    onClick={() => handleCalendarToggle(calendar.id)}
                    className="flex items-center gap-3 p-4 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => { }}
                        className="w-4 h-4 rounded-full cursor-pointer accent-primary"
                      />
                    </div>
                    <span className="text-[14px] font-medium text-white">
                      {displayName}
                    </span>
                  </div>
                )
              })}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleClose}
                className="!bg-transparent !border !border-white/20 hover:!bg-white/5 flex-1 !rounded-lg"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                isLoading={isSubmitLoading}
                className="flex-1 !rounded-lg"
              >
                Confirm
              </Button>
            </div>
          </div>
        )}
      </>
    </Modal>
  )
}